import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Bot, Sparkles, Microscope, GitBranch, Brain, Search, Loader2, Send, TrendingDown, AlertTriangle, History, Lightbulb, Activity, Heart, Clock, Mail, BookOpen, Save } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==================== SHARED COMPONENTS ====================

function AIOpsShell({ title, description, icon: Icon, children }) {
  return (
    <div className="space-y-5" data-testid="aiops-page">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-purple-50 border border-purple-200"><Icon className="h-5 w-5 text-purple-600" /></div>
        <div>
          <h2 className="text-lg font-semibold">{title}</h2>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

function NoAIConfig() {
  return (
    <Card className="border-dashed border-2">
      <CardContent className="flex flex-col items-center justify-center py-12">
        <Brain className="h-12 w-12 text-muted-foreground/30 mb-3" />
        <p className="font-medium text-muted-foreground">Nenhuma integração de IA configurada</p>
        <p className="text-sm text-muted-foreground mt-1">Configure em Gerenciamento &rarr; Integrações &rarr; IA</p>
      </CardContent>
    </Card>
  );
}

function useAICheck() {
  const [hasAI, setHasAI] = useState(null);
  const { getAuthHeader } = useAuthStore();
  useEffect(() => {
    axios.get(`${API}/integrations`, { headers: getAuthHeader() })
      .then(res => setHasAI((res.data.integrations || []).some(i => i.category === 'ai' && i.is_active)))
      .catch(() => setHasAI(false));
  }, [getAuthHeader]);
  return hasAI;
}

function TargetSelector({ groupId, setGroupId, hostId, setHostId, groups, hosts, onGroupChange }) {
  return (
    <div className="flex gap-3">
      <Select value={groupId} onValueChange={(v) => { setGroupId(v); setHostId(''); if (onGroupChange) onGroupChange(v); }}>
        <SelectTrigger className="w-64"><SelectValue placeholder="Todos os grupos" /></SelectTrigger>
        <SelectContent className="max-h-60">
          <SelectItem value="all">Todos os grupos</SelectItem>
          {groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={hostId} onValueChange={setHostId}>
        <SelectTrigger className="w-64"><SelectValue placeholder="Todos os hosts" /></SelectTrigger>
        <SelectContent className="max-h-60">
          <SelectItem value="all">Todos os hosts</SelectItem>
          {hosts.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function AIResponse({ result, loading }) {
  if (loading) return <Card><CardContent className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-purple-500" /><span className="ml-3 text-muted-foreground">Analisando com IA...</span></CardContent></Card>;
  if (!result) return null;
  if (result.error) return <Card className="border-red-200"><CardContent className="p-4 text-red-600 text-sm">{result.error}</CardContent></Card>;
  return <Card><CardContent className="p-6"><div className="prose prose-sm max-w-none dark:prose-invert whitespace-pre-wrap text-sm">{result.response}</div></CardContent></Card>;
}

function useTargetSelector() {
  const [groups, setGroups] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [groupId, setGroupId] = useState('all');
  const [hostId, setHostId] = useState('all');
  const { getAuthHeader } = useAuthStore();

  useEffect(() => {
    axios.get(`${API}/aiops/hostgroups-simple`, { headers: getAuthHeader() })
      .then(res => setGroups(res.data.groups || []))
      .catch(() => {});
  }, [getAuthHeader]);

  const loadHosts = useCallback((gid) => {
    const url = gid && gid !== 'all' ? `${API}/aiops/hosts-simple?group_id=${gid}` : `${API}/aiops/hosts-simple`;
    axios.get(url, { headers: getAuthHeader() }).then(res => setHosts(res.data.hosts || [])).catch(() => {});
  }, [getAuthHeader]);

  useEffect(() => { loadHosts(groupId); }, [groupId, loadHosts]);

  return { groups, hosts, groupId, setGroupId, hostId, setHostId, loadHosts };
}

// ==================== AGENTS PAGE (Chat + Management) ====================

export function AIAgentsPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('default');
  const [showManage, setShowManage] = useState(false);
  const [editAgent, setEditAgent] = useState(null);
  const [agentForm, setAgentForm] = useState({ name: '', description: '', system_prompt: '', is_active: true });
  const [saving, setSaving] = useState(false);
  const hasAI = useAICheck();
  const { getAuthHeader } = useAuthStore();

  const fetchAgents = useCallback(() => {
    axios.get(`${API}/aiops/agents`, { headers: getAuthHeader() })
      .then(res => setAgents(res.data.agents || []))
      .catch(() => {});
  }, [getAuthHeader]);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  // Auto-select first agent when loaded
  useEffect(() => {
    if (agents.length > 0 && selectedAgent === 'default') {
      setSelectedAgent(agents[0].id);
    }
  }, [agents, selectedAgent]);

  const handleSend = async () => {
    if (!input.trim()) return;
    setMessages(prev => [...prev, { role: 'user', content: input }]);
    const msg = input; setInput(''); setSending(true);
    try {
      const body = { message: msg };
      if (selectedAgent && selectedAgent !== 'default') body.agent_id = selectedAgent;
      const res = await axios.post(`${API}/aiops/chat`, body, { headers: getAuthHeader() });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Erro: ${err.response?.data?.detail || 'Falha'}` }]);
    } finally { setSending(false); }
  };

  const handleSaveAgent = async () => {
    setSaving(true);
    try {
      if (editAgent) {
        await axios.put(`${API}/aiops/agents/${editAgent.id}`, agentForm, { headers: getAuthHeader() });
      } else {
        await axios.post(`${API}/aiops/agents`, agentForm, { headers: getAuthHeader() });
      }
      setEditAgent(null);
      setAgentForm({ name: '', description: '', system_prompt: '', is_active: true });
      fetchAgents();
      toast.success(editAgent ? 'Agente atualizado' : 'Agente criado');
    } catch (err) { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const handleDeleteAgent = async (id) => {
    if (!window.confirm('Excluir este agente?')) return;
    await axios.delete(`${API}/aiops/agents/${id}`, { headers: getAuthHeader() });
    fetchAgents();
    toast.success('Agente excluído');
  };

  if (hasAI === null) return <Skeleton className="h-[400px] w-full" />;
  if (!hasAI) return <AIOpsShell title="Agentes AI" description="Chat inteligente" icon={Bot}><NoAIConfig /></AIOpsShell>;

  return (
    <AIOpsShell title="Agentes AI" description="Converse com a IA sobre sua infraestrutura — dados consultados em tempo real do Zabbix" icon={Bot}>
      {/* Agent selector + manage button */}
      <div className="flex items-center gap-3">
        <Select value={selectedAgent} onValueChange={(v) => { setSelectedAgent(v); setMessages([]); }}>
          <SelectTrigger className="w-72"><SelectValue placeholder="Selecionar agente" /></SelectTrigger>
          <SelectContent>
            {agents.filter(a => a.is_active).map(a => (
              <SelectItem key={a.id} value={a.id}>
                <span className="flex items-center gap-2">
                  {a.name}
                  {a.is_default && <span className="text-xs text-muted-foreground">(padrão)</span>}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => setShowManage(true)}>Gerenciar Agentes</Button>
      </div>

      {/* Chat area */}
      <Card className="flex flex-col" style={{ height: 'calc(100vh - 280px)' }}>
        <ScrollArea className="flex-1 p-4">
          <div className="space-y-3">
            {messages.length === 0 && (() => {
              const currentAgent = agents.find(a => a.id === selectedAgent);
              const agentName = currentAgent?.name || 'Agente Padrão';
              const suggestions = {
                'Agente Padrão': ['Resumo da infraestrutura', 'Quais hosts estão com mais problemas?', 'Análise de disponibilidade', 'Status geral'],
                'Agente Incidente': ['Faça um RCA dos incidentes ativos', 'Correlacione os alertas atuais', 'Qual a causa raiz mais provável?', 'Gere um playbook de remediação'],
                'Agente Especialista': ['Planejamento de capacidade', 'Recomendações de tuning', 'Melhorias de observabilidade', 'Análise de arquitetura'],
                'Agente Consulta': ['Quantos hosts tem no grupo XIPP?', 'Quais alertas estão ativos?', 'Liste os grupos de hosts', 'Hosts com mais problemas'],
              };
              const qs = suggestions[agentName] || suggestions['Agente Padrão'];
              return (
                <div className="text-center py-12 text-muted-foreground">
                  <Bot className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm font-medium mb-1">{agentName}</p>
                  <p className="text-xs mb-4">{currentAgent?.description || 'Consulta dados do Zabbix em tempo real'}</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {qs.map(q => (
                      <Button key={q} variant="outline" size="sm" className="text-xs" onClick={() => setInput(q)}>{q}</Button>
                    ))}
                  </div>
                </div>
              );
            })()}
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-lg p-3 text-sm ${msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                  <pre className="whitespace-pre-wrap font-sans">{msg.content}</pre>
                </div>
              </div>
            ))}
            {sending && <div className="flex justify-start"><div className="bg-muted rounded-lg p-3 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /><span className="text-xs text-muted-foreground">Consultando Zabbix e analisando...</span></div></div>}
          </div>
        </ScrollArea>
        <div className="border-t p-3 flex gap-2">
          <Textarea placeholder="Pergunte algo — a IA vai buscar os dados no Zabbix..." value={input} onChange={(e) => setInput(e.target.value)} className="min-h-[44px] max-h-[100px] resize-none" onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }} />
          <Button onClick={handleSend} disabled={sending || !input.trim()} className="shrink-0"><Send className="h-4 w-4" /></Button>
        </div>
      </Card>

      {/* Manage Agents Dialog */}
      <Dialog open={showManage} onOpenChange={setShowManage}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader><DialogTitle>Gerenciar Agentes AI</DialogTitle></DialogHeader>
          <ScrollArea className="flex-1">
            <div className="space-y-4 pr-3">
              {/* Existing agents */}
              {agents.map(a => (
                <Card key={a.id}>
                  <CardContent className="p-3 flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm">{a.name}</p>
                        <Badge variant={a.is_active ? 'default' : 'secondary'} className="text-xs">{a.is_active ? 'Ativo' : 'Inativo'}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{a.description || 'Sem descrição'}</p>
                      <p className="text-xs text-muted-foreground mt-1 font-mono line-clamp-2">{a.system_prompt?.substring(0, 100)}...</p>
                    </div>
                    <div className="flex gap-1 shrink-0 ml-3">
                      <Button variant="ghost" size="sm" onClick={() => { setEditAgent(a); setAgentForm({ name: a.name, description: a.description || '', system_prompt: a.system_prompt || '', is_active: a.is_active }); }}>Editar</Button>
                      <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteAgent(a.id)}>Excluir</Button>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Create/Edit form */}
              <Card className="border-dashed border-2">
                <CardContent className="p-4 space-y-3">
                  <p className="text-sm font-semibold">{editAgent ? `Editando: ${editAgent.name}` : 'Novo Agente'}</p>
                  <input className="w-full border rounded px-3 py-2 text-sm" placeholder="Nome do agente" value={agentForm.name} onChange={(e) => setAgentForm({ ...agentForm, name: e.target.value })} />
                  <input className="w-full border rounded px-3 py-2 text-sm" placeholder="Descrição curta" value={agentForm.description} onChange={(e) => setAgentForm({ ...agentForm, description: e.target.value })} />
                  <Textarea rows={6} placeholder="System prompt — defina como o agente deve se comportar, que variáveis usar, tom de resposta...&#10;&#10;Variáveis disponíveis: Os dados do Zabbix são injetados automaticamente no contexto." value={agentForm.system_prompt} onChange={(e) => setAgentForm({ ...agentForm, system_prompt: e.target.value })} className="font-mono text-xs" />
                  <div className="flex items-center gap-3">
                    <Button onClick={handleSaveAgent} disabled={saving || !agentForm.name}>
                      {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{editAgent ? 'Salvar' : 'Criar Agente'}
                    </Button>
                    {editAgent && <Button variant="outline" onClick={() => { setEditAgent(null); setAgentForm({ name: '', description: '', system_prompt: '', is_active: true }); }}>Cancelar</Button>}
                  </div>
                </CardContent>
              </Card>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </AIOpsShell>
  );
}

// ==================== ANALYSIS PAGES ====================

function AnalysisPage({ title, description, icon, analysisType, buttonLabel }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const hasAI = useAICheck();
  const { getAuthHeader } = useAuthStore();
  const { groups, hosts, groupId, setGroupId, hostId, setHostId, loadHosts } = useTargetSelector();
  const [days, setDays] = useState('7');

  const run = async () => {
    setLoading(true); setResult(null);
    try {
      const body = { type: analysisType, days: parseInt(days) };
      if (hostId && hostId !== 'all') body.host_id = hostId;
      else if (groupId && groupId !== 'all') body.group_ids = [groupId];
      const res = await axios.post(`${API}/aiops/analysis`, body, { headers: getAuthHeader() });
      setResult(res.data);
    } catch (err) { setResult({ error: err.response?.data?.detail || 'Erro na análise' }); }
    finally { setLoading(false); }
  };

  if (hasAI === null) return <Skeleton className="h-[400px] w-full" />;
  if (!hasAI) return <AIOpsShell title={title} description={description} icon={icon}><NoAIConfig /></AIOpsShell>;

  return (
    <AIOpsShell title={title} description={description} icon={icon}>
      <Card>
        <CardContent className="p-4 space-y-4">
          <TargetSelector groupId={groupId} setGroupId={setGroupId} hostId={hostId} setHostId={setHostId} groups={groups} hosts={hosts} onGroupChange={loadHosts} />
          <div className="flex items-center gap-3">
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">24 horas</SelectItem>
                <SelectItem value="7">7 dias</SelectItem>
                <SelectItem value="30">30 dias</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={run} disabled={loading} className="gap-2">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : React.createElement(icon, { className: "h-4 w-4" })}
              {buttonLabel}
            </Button>
          </div>
        </CardContent>
      </Card>
      <AIResponse result={result} loading={loading} />
    </AIOpsShell>
  );
}

export function AIAnalysisPage() {
  return <AnalysisPage title="Análise Completa" description="Análise geral da infraestrutura ou host específico" icon={Sparkles} analysisType="full" buttonLabel="Executar Análise" />;
}

export function AIGranularPage() {
  return <AnalysisPage title="Análise Granular" description="Análise detalhada por host, trigger e severidade" icon={Microscope} analysisType="granular" buttonLabel="Analisar" />;
}

export function AICorrelationPage() {
  return <AnalysisPage title="Correlação de Eventos" description="IA identifica padrões e relações entre incidentes" icon={GitBranch} analysisType="correlation" buttonLabel="Correlacionar" />;
}

export function AIPredictionPage() {
  return <AnalysisPage title="Predição" description="Previsão de incidentes com base em padrões" icon={Brain} analysisType="prediction" buttonLabel="Gerar Predições" />;
}

// ==================== RCA - Select Incident ====================

export function AIRCAPage() {
  const [incidents, setIncidents] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingIncidents, setLoadingIncidents] = useState(true);
  const hasAI = useAICheck();
  const { getAuthHeader } = useAuthStore();

  useEffect(() => {
    axios.get(`${API}/incidents?use_triggers=true`, { headers: getAuthHeader() })
      .then(res => setIncidents(res.data.incidents || []))
      .catch(() => {})
      .finally(() => setLoadingIncidents(false));
  }, [getAuthHeader]);

  const run = async () => {
    if (!selectedId) { toast.error('Selecione um incidente'); return; }
    setLoading(true); setResult(null);
    try {
      const res = await axios.post(`${API}/aiops/analysis`, { type: 'rca', incident_id: selectedId }, { headers: getAuthHeader() });
      setResult(res.data);
    } catch (err) { setResult({ error: err.response?.data?.detail || 'Erro' }); }
    finally { setLoading(false); }
  };

  if (hasAI === null) return <Skeleton className="h-[400px] w-full" />;
  if (!hasAI) return <AIOpsShell title="RCA Automático" description="Análise de causa raiz" icon={Search}><NoAIConfig /></AIOpsShell>;

  const SEVERITY_COLORS = { 0: '#97AAB3', 1: '#3B82F6', 2: '#EAB308', 3: '#F97316', 4: '#EF4444', 5: '#DC2626' };

  return (
    <AIOpsShell title="RCA Automático" description="Selecione um incidente para análise de causa raiz por IA" icon={Search}>
      <Card>
        <CardContent className="p-4 space-y-3">
          {loadingIncidents ? <Skeleton className="h-10 w-full" /> : (
            <Select value={selectedId} onValueChange={setSelectedId}>
              <SelectTrigger><SelectValue placeholder="Selecione um incidente ativo..." /></SelectTrigger>
              <SelectContent className="max-h-72">
                {incidents.map(inc => (
                  <SelectItem key={inc.id} value={inc.id}>
                    <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ backgroundColor: SEVERITY_COLORS[inc.severity] }} />
                    {inc.name} — {inc.host_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button onClick={run} disabled={loading || !selectedId} className="gap-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Executar RCA
          </Button>
        </CardContent>
      </Card>
      <AIResponse result={result} loading={loading} />
    </AIOpsShell>
  );
}

// ==================== HISTORY ====================

export function AIHistoryPage() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const { getAuthHeader } = useAuthStore();

  useEffect(() => {
    axios.get(`${API}/aiops/history?limit=50`, { headers: getAuthHeader() })
      .then(res => setHistory(res.data.history || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [getAuthHeader]);

  const TYPE_LABELS = { full: 'Análise Completa', granular: 'Granular', correlation: 'Correlação', prediction: 'Predição', rca: 'RCA', chat: 'Chat' };

  if (loading) return <Skeleton className="h-[400px] w-full" />;

  return (
    <AIOpsShell title="Histórico de Análises" description="Consulte análises anteriores realizadas pela IA" icon={History}>
      {history.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">Nenhuma análise realizada ainda</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {history.map((h) => (
            <Card key={h.id} className="cursor-pointer hover:shadow-sm transition-shadow" onClick={() => setExpanded(expanded === h.id ? null : h.id)}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">{TYPE_LABELS[h.type] || h.type}</Badge>
                    <span className="text-sm font-medium truncate max-w-md">{h.target || h.prompt_summary}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{formatDate(h.created_at)}</span>
                </div>
                {expanded === h.id && (
                  <div className="mt-3 pt-3 border-t">
                    <pre className="whitespace-pre-wrap text-sm font-sans text-muted-foreground">{h.response}</pre>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </AIOpsShell>
  );
}

// ==================== HEALTH SCORES ====================

export function AIHealthScoresPage() {
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const { getAuthHeader } = useAuthStore();

  useEffect(() => {
    axios.get(`${API}/aiops/health-scores`, { headers: getAuthHeader() })
      .then(res => setScores(res.data.scores || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [getAuthHeader]);

  if (loading) return <Skeleton className="h-[400px] w-full" />;

  const statusColors = { healthy: 'bg-emerald-500', warning: 'bg-amber-500', critical: 'bg-red-500' };

  return (
    <AIOpsShell title="Score de Saúde" description="Score 0-100 para cada host baseado em incidentes e severidade" icon={Heart}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {scores.map(s => (
          <Card key={s.host_id} className="border">
            <CardContent className="p-3 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold ${statusColors[s.status]}`}>{s.score}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{s.name}</p>
                <p className="text-xs text-muted-foreground truncate">{s.groups}</p>
              </div>
              {s.problems > 0 && <Badge className="bg-red-500 text-white text-xs shrink-0">{s.problems}</Badge>}
            </CardContent>
          </Card>
        ))}
        {scores.length === 0 && <p className="text-muted-foreground col-span-3 text-center py-8">Nenhum host encontrado</p>}
      </div>
    </AIOpsShell>
  );
}

// ==================== ANOMALIES ====================

export function AIAnomaliesPage() {
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('7');
  const { getAuthHeader } = useAuthStore();

  const fetch = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/aiops/anomalies?days=${days}`, { headers: getAuthHeader() })
      .then(res => setAnomalies(res.data.anomalies || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [getAuthHeader, days]);

  useEffect(() => { fetch(); }, [fetch]);

  return (
    <AIOpsShell title="Detecção de Anomalias" description="Hosts com padrões incomuns de incidentes" icon={AlertTriangle}>
      <div className="flex gap-3 items-center">
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 dias</SelectItem>
            <SelectItem value="14">14 dias</SelectItem>
            <SelectItem value="30">30 dias</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {loading ? <Skeleton className="h-[200px] w-full" /> : anomalies.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">Nenhuma anomalia detectada no período</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {anomalies.map((a, i) => (
            <Card key={i} className={a.severity === 'high' ? 'border-red-200' : 'border-amber-200'}>
              <CardContent className="p-3 flex items-center gap-3">
                <AlertTriangle className={`h-5 w-5 shrink-0 ${a.severity === 'high' ? 'text-red-500' : 'text-amber-500'}`} />
                <div className="flex-1">
                  <p className="text-sm font-medium">{a.name}</p>
                  <p className="text-xs text-muted-foreground">{a.description}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-bold">{a.previous} &rarr; {a.current}</p>
                  <Badge variant="outline" className="text-xs">{a.type === 'new_issues' ? 'Novo' : 'Spike'}</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </AIOpsShell>
  );
}


// ==================== HISTORICAL ANALYSIS ====================

export function AIHistoricalAnalysisPage() {
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState(null);
  const [analysisId, setAnalysisId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('7');
  const [showEmail, setShowEmail] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailList, setEmailList] = useState([]);
  const [sending, setSending] = useState(false);
  const [savingKB, setSavingKB] = useState(false);
  const hasAI = useAICheck();
  const { getAuthHeader } = useAuthStore();

  const run = async () => {
    setLoading(true); setResult(null); setStats(null); setAnalysisId(null);
    try {
      const res = await axios.post(`${API}/aiops/historical-analysis`, { type: 'historical', days: parseInt(period) }, { headers: getAuthHeader() });
      setResult(res.data.response);
      setStats(res.data.stats);
      setAnalysisId(res.data.analysis_id);
    } catch (err) {
      setResult(`Erro: ${err.response?.data?.detail || 'Falha na análise'}`);
    } finally { setLoading(false); }
  };

  const addEmail = () => {
    if (!emailTo || !emailTo.includes('@')) return;
    if (!emailList.includes(emailTo)) setEmailList([...emailList, emailTo]);
    setEmailTo('');
  };

  const handleSendEmail = async () => {
    if (emailList.length === 0 || !analysisId) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/aiops/historical-analysis/${analysisId}/send-email`, { recipients: emailList }, { headers: getAuthHeader() });
      toast.success(res.data.message);
      setShowEmail(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao enviar'); }
    finally { setSending(false); }
  };

  const handleSaveKB = async () => {
    if (!result) return;
    setSavingKB(true);
    try {
      await axios.post(`${API}/kb/solutions`, {
        title: `Plano de Ação — Análise Histórica ${period} dias (${new Date().toLocaleDateString('pt-BR')})`,
        content: result,
        category: 'Infraestrutura',
        tags: ['plano-de-acao', 'historico', `${period}d`, 'AI'],
        related_triggers: [],
      }, { headers: getAuthHeader() });
      toast.success('Plano salvo na Base de Conhecimento');
    } catch (err) { toast.error('Erro ao salvar'); }
    finally { setSavingKB(false); }
  };

  if (hasAI === null) return <Skeleton className="h-[400px] w-full" />;
  if (!hasAI) return <AIOpsShell title="Análise de Histórico" description="Análise profunda com plano de ação" icon={Clock}><NoAIConfig /></AIOpsShell>;

  const PERIODS = [
    { value: '7', label: '7 dias' },
    { value: '30', label: '30 dias' },
    { value: '60', label: '60 dias' },
    { value: '90', label: '90 dias' },
  ];

  return (
    <AIOpsShell title="Análise de Histórico" description="Análise completa de incidentes com plano de ação técnico" icon={Clock}>
      {/* Controls */}
      <Card>
        <CardContent className="p-4 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Período:</span>
            {PERIODS.map(p => (
              <Button key={p.value} variant={period === p.value ? 'default' : 'outline'} size="sm" onClick={() => setPeriod(p.value)}>{p.label}</Button>
            ))}
          </div>
          <Button onClick={run} disabled={loading} className="ml-auto gap-2 bg-purple-600 hover:bg-purple-700">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Gerar Análise e Plano de Ação
          </Button>
        </CardContent>
      </Card>

      {/* Loading */}
      {loading && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Loader2 className="h-10 w-10 animate-spin text-purple-500 mb-4" />
            <p className="text-sm font-medium">Analisando {period} dias de incidentes...</p>
            <p className="text-xs text-muted-foreground mt-1">Coletando dados, correlacionando eventos e gerando plano de ação</p>
          </CardContent>
        </Card>
      )}

      {/* Stats Summary */}
      {stats && !loading && (
        <div className="grid grid-cols-4 gap-3">
          <Card className="border-red-200 bg-red-50/50"><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-600">{stats.total_events}</p>
            <p className="text-xs text-muted-foreground">Incidentes</p>
          </CardContent></Card>
          <Card className="border-green-200 bg-green-50/50"><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.resolved}</p>
            <p className="text-xs text-muted-foreground">Resolvidos</p>
          </CardContent></Card>
          <Card className="border-blue-200 bg-blue-50/50"><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.mttr_min}min</p>
            <p className="text-xs text-muted-foreground">MTTR Médio</p>
          </CardContent></Card>
          <Card className="border-purple-200 bg-purple-50/50"><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-purple-600">{stats.period_days}d</p>
            <p className="text-xs text-muted-foreground">Período</p>
          </CardContent></Card>
        </div>
      )}

      {/* Result */}
      {result && !loading && (
        <>
          <Card className="border-purple-200">
            <CardContent className="p-6">
              <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed">{result}</pre>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => { setResult(null); setStats(null); }} className="gap-2">
              <Sparkles className="h-4 w-4" />Nova Análise
            </Button>
            <Button onClick={handleSaveKB} disabled={savingKB} className="gap-2">
              {savingKB ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookOpen className="h-4 w-4" />}
              Salvar na Base de Conhecimento
            </Button>
            <Button variant="outline" onClick={() => setShowEmail(true)} className="gap-2">
              <Mail className="h-4 w-4" />Enviar por Email
            </Button>
          </div>
        </>
      )}

      {/* Email Dialog */}
      <Dialog open={showEmail} onOpenChange={setShowEmail}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Enviar Plano de Ação por Email</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="email@empresa.com"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }} />
              <Button variant="outline" onClick={addEmail}>Adicionar</Button>
            </div>
            {emailList.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {emailList.map(e => (
                  <Badge key={e} variant="secondary" className="text-xs pr-1 flex items-center gap-1">
                    {e}<button onClick={() => setEmailList(emailList.filter(x => x !== e))} className="hover:bg-muted rounded p-0.5 ml-1">&times;</button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmail(false)}>Cancelar</Button>
            <Button onClick={handleSendEmail} disabled={sending || emailList.length === 0}>
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AIOpsShell>
  );
}
